/**
 * The two class groups (LOCKER-421 · `DoD8`)
 * =========================================
 *
 * A drift class and a counted-never-drift class are different kinds of thing, and
 * today's console gives them the same weight — one flat row of chips, all styled
 * alike. That is the specific problem this file exists to fix.
 *
 * **Solid border = counted in `driftTotal`. Dashed border = counted separately.**
 * The border IS the affordance, and it is worded on the total rather than on
 * writability because writability is not a property of the group: `onlyInSst`,
 * `onlyInSl2` and `onlyInOlap` are all solid and all reported-never-written, and
 * `olapStale` is solid and deliberately unwritable until its false-positive rate
 * has been measured against a real OLAP row. What the two groups DO partition
 * cleanly is the drift total, which is the number an operator acts on. Whether a
 * given class can be written is on its own note, per class, where it is true.
 *
 * Shared between the drift table's filters and the overview's panel so the two
 * cannot disagree about which group a class is in — a class that filtered as
 * writable but rendered as counted-never-drift would be worse than either.
 */

/** Classes that sum to `driftTotal`. Not all of them are writable — see each note. */
export const DRIFT_CLASS_KEYS = [
  "statusMismatch",
  "fieldMismatch",
  "olapMissing",
  "olapStale",
  "onlyInOlap",
  "onlyInSst",
  "onlyInSl2",
] as const;

/** Classes counted separately and NEVER summed into `driftTotal`. */
export const NON_DRIFT_KEYS = [
  "unjoinable",
  "duplicateInSl2",
  "orphanReservation",
  "poisonedExpiry",
  "knownProjectionDefect",
  "partnerBlankField",
  "migratedV1History",
  "retiredV2Husk",
] as const;

export type DriftClassKey = (typeof DRIFT_CLASS_KEYS)[number]
export type NonDriftKey = (typeof NON_DRIFT_KEYS)[number]

/** Which of the writable classes a sync can actually close, and how. */
export const DRIFT_CLASS_NOTE: Record<string, string> = {
  statusMismatch:
    "The partner and our snapshot disagree on status, compared in the v1 vocabulary. Writable.",
  fieldMismatch:
    "They disagree on one or more compared fields. Writable, on the compared fields only.",
  olapMissing:
    "The row is in DynamoDB but not in OLAP — the stream did not deliver. Writable, by touching the Thing so the projection re-drives.",
  olapStale:
    "OLAP holds the row, but its projection disagrees with our snapshot on a column the known LOCKER-351 defect does not own. The same touch would close it, but the comparison has not yet run against a real OLAP row — reported-only until its false-positive rate is known.",
  onlyInOlap:
    "OLAP holds a reservation neither the partner nor DynamoDB has. There is no Thing to touch and writing to OLAP is out of scope, so nothing here can close it. Reported, never written.",
  onlyInSst:
    "The partner has it and we have no Thing. Materialising a row is a different act with its own machinery. Reported, never written.",
  onlyInSl2:
    "We have it and the partner does not. It may be a genuine local record, and deleting is irreversible. Reported, never written.",
  identical: "All present sources agree on every compared column.",
  duplicateInSl2:
    "Two Things on one native id. Decided before any field compare, so a sync can never make the duplicate permanent and consistent.",
}

export const NON_DRIFT_NOTE: Record<NonDriftKey, string> = {
  unjoinable:
    "Our Thing carries no native SST id. There is nothing to join on, so it cannot be drift.",
  duplicateInSl2:
    "Two Things on one native id. Classified before any field compare, so a sync can never reconcile both copies into agreement.",
  orphanReservation:
    "A v2 row whose door_id resolves to no locker in the partner's own door list.",
  poisonedExpiry:
    "Expiry precedes creation, so the window is inverted. The partner value is wrong at source — roughly 1.6% of production, and growing.",
  knownProjectionDefect:
    "Differs from OLAP only on the columns a known, filed projection bug owns. Labelled so the noise is attributable, never filtered so it stays visible.",
  partnerBlankField:
    "The partner holds no value for a compared field DynamoDB does — SST's v1→v2 migration dropped delivery, reception and admin times. DynamoDB keeps its value; no sync ever blanks it.",
  migratedV1History:
    "SST v1 history on a locker already migrated to v2. Its copies in SL2 now live on the v2 locker, so it is not drift (LOCKER-444).",
  retiredV2Husk:
    "A v2 reservation whose SL2 copy was an adopt- husk the SL1 import retired. Withdrawn on purpose, so it is not drift (LOCKER-448).",
}

/** Short labels, because the filter row is tight and the ids are long. */
export const NON_DRIFT_LABEL: Record<NonDriftKey, string> = {
  unjoinable: "unjoinable",
  duplicateInSl2: "duplicateInSl2",
  orphanReservation: "orphan",
  poisonedExpiry: "poisonedExpiry",
  knownProjectionDefect: "knownProjectionDefect",
  partnerBlankField: "partnerBlankField",
  migratedV1History: "migratedV1History",
  retiredV2Husk: "retiredV2Husk",
}

export const isNonDriftKey = (key: string): key is NonDriftKey =>
  (NON_DRIFT_KEYS as readonly string[]).includes(key)
